// Chunk-tuning presets and user-facing guide.
//
// Single source of truth for the doc-type presets the Ingestion form
// surfaces in its dropdown, plus the body copy of the inline
// "Show recommendations" panel and the standalone docs page.
//
// Editing this file is how product / docs updates the guidance —
// no other component should hardcode chunk-size numbers or copy.

export const DEFAULT_CHUNK_SIZE = 2000;
export const DEFAULT_CHUNK_OVERLAP = 250;
export const DEFAULT_PRESET_ID = "general";


// Presets shown in the dropdown above the chunk sliders. Picking a
// preset snaps both sliders to its values; the user can still drag
// after to fine-tune. The Save-as-agent-default button persists
// (preset, chunk_size, chunk_overlap) onto agent.flow_config.ingestion.
export const CHUNK_PRESETS = [
  {
    id: "general",
    label: "General prose",
    chunk_size: 2000,
    chunk_overlap: 250,
    short_why: "The default for most teams.",
  },
  {
    id: "contracts",
    label: "Contracts, policies, individual clauses",
    chunk_size: 800,
    chunk_overlap: 100,
    short_why: "Each clause has its own meaning. Smaller chunks let search find the exact right paragraph instead of a whole section.",
  },
  {
    id: "runbooks",
    label: "Long-form runbooks, end-to-end procedures",
    chunk_size: 3000,
    chunk_overlap: 400,
    short_why: "Questions like 'walk me through the full setup' benefit from broader context per chunk.",
  },
  {
    id: "code",
    label: "Code, API references, technical specs",
    chunk_size: 800,
    chunk_overlap: 50,
    short_why: "Functions are short and self-contained. Large chunks would mix unrelated symbols.",
  },
  {
    id: "multilingual",
    label: "Non-Latin languages (Chinese, Japanese, Arabic)",
    chunk_size: 1200,
    chunk_overlap: 200,
    short_why: "These scripts pack more meaning per character; smaller chunks balance things out.",
  },
];

export function presetById(id) {
  return CHUNK_PRESETS.find(p => p.id === id) || CHUNK_PRESETS[0];
}

// Snap a (chunk_size, chunk_overlap) pair back to a preset id when
// they match exactly. Used to seed the dropdown when an agent's saved
// flow_config.ingestion happens to match a preset (so we can show the
// label rather than "Custom").
export function presetIdForValues(chunk_size, chunk_overlap) {
  const match = CHUNK_PRESETS.find(
    p => p.chunk_size === chunk_size && p.chunk_overlap === chunk_overlap
  );
  return match ? match.id : "custom";
}


// ─── Guide content (rendered in both the inline panel and docs page) ─────
//
// Plain object — not markdown — so the rendering component controls layout
// (table headers, bullet styling, callouts) without an extra MD library.
// Edit copy in place; rendering will pick it up.

export const TUNING_GUIDE = {
  // Pitch / what is this about
  headline: "Tuning your knowledge base",
  intro:
    "When you upload documents, the platform splits them into smaller chunks before " +
    "indexing. The size of those chunks shapes how well search works.",

  // The default + the three reasons it's the default
  default_section: {
    heading: "Recommended default",
    summary: "Chunk size: 2,000 characters · Overlap: 250 characters",
    body: "This setting works for most documents. It balances three things:",
    points: [
      {
        title: "Search quality",
        text:
          "Modern AI embedding models perform best when each chunk is around 500 words. " +
          "Too small, and chunks lose their surrounding context. Too big, and the meaningful " +
          "signal gets diluted.",
      },
      {
        title: "Context awareness",
        text:
          "The platform automatically prepends a short summary to each chunk so the model " +
          "knows where it sits in the document. Keeping chunks at this size leaves room for " +
          "that summary without truncating your content.",
      },
      {
        title: "Cost and speed",
        text:
          "Less overlap means fewer total chunks, which means cheaper indexing and faster " +
          "queries. The platform's reranker compensates for the few edge cases that more " +
          "overlap would otherwise catch.",
      },
    ],
  },

  // The when-to-deviate table — derived from CHUNK_PRESETS so they stay in sync
  deviation_section: {
    heading: "When to deviate",
    intro: "Use one of these presets if your corpus matches:",
    rows: CHUNK_PRESETS.filter(p => p.id !== "general").map(p => ({
      label: p.label,
      values: `${p.chunk_size.toLocaleString()} / ${p.chunk_overlap}`,
      why: p.short_why,
    })).concat([{
      label: "Mixed or unsure",
      values: "Default (2,000 / 250)",
      why: "When in doubt.",
    }]),
  },

  // The biggest tip
  per_agent_callout: {
    heading: "The bigger win: set it per agent, not per upload",
    body:
      "Don't fiddle with the slider on every upload. Save the right values as the agent's " +
      "default once, and every future upload to that agent inherits them. You can still " +
      "override on individual documents that don't fit the pattern.",
  },

  // What's automatic — reduces user anxiety about tuning
  automatic_section: {
    heading: "What you don't need to worry about",
    items: [
      {
        title: "Where chunks split.",
        text: "The platform automatically breaks at sentence and paragraph boundaries — never mid-word.",
      },
      {
        title: "Pages with screenshots.",
        text: "The parser keeps each figure with its surrounding text in the same chunk.",
      },
      {
        title: "Finding the \u201cperfect\u201d number.",
        text: "The reranker forgives small misses. Only deviate if you've tested and seen a real difference.",
      },
    ],
  },

  // Verification path — points users at the eval tab so this isn't blind
  verification_section: {
    heading: "How to verify your choice",
    body:
      "The Eval tab lets you run a set of test questions against an agent and see the " +
      "accuracy score. If you're tuning chunk size, run the eval at the current setting, " +
      "change it, re-ingest your documents, and run the eval again. If accuracy improved, " +
      "keep it. If not, revert.",
    cta: { label: "Open Eval tab", path: "/admin?tab=eval" },
  },
};
